import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap";
import { FieldArray, Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import * as _ from "lodash";

import profile from "../../assets/images/profile.png";
import {
  ActivityButton,
  AppRichEditor,
  TimeInput,
} from "../../components/Common";
import {
  CreateQuestionRequest,
  ParsedError,
  QuestionImageUploadResponse,
} from "../../interfaces";
import {
  ErrorHandlingTypeEnum,
  QuestionModelEnum,
  QuestionOptionEnum,
} from "../../enums";
import {
  useGetQuestionQuery,
  useAddQuestionMutation,
  useUpdateQuestionMutation,
  useUploadQuestionImageMutation,
} from "../../store/api/questionApi";
import { toast } from "react-toastify";
import { IMAGE_ACCEPT } from "../../constants";
import { generateUniqueId } from "../../services";
import { QuestionContentDto } from "../../interfaces/question/question-content-dto.interface";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
  subjectId: string;
  model: QuestionModelEnum;
};
export const AddEditQuestionsModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
  model,
}) => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  const formRef: any = useRef();

  const [initialValues, setInitialValues] = useState<CreateQuestionRequest>({
    contents: [],
    model,
    timeDuration: "3:00",
    marks: 1,
    displayImageId: "",
    lessonId,
    skills: [],
    optionA: { en: "", ar: "" },
    optionB: { en: "", ar: "" },
    optionC: { en: "", ar: "" },
    optionD: { en: "", ar: "" },
    correctOption: QuestionOptionEnum.OPTION_A,
  });

  //#region internal state
  const [imagesProgress, setImagesProgress] = useState<
    {
      isLoading: boolean;
      isSuccess: boolean;
      isError: boolean;
      error: any;
      response: QuestionImageUploadResponse | null;
      tempId?: string;
      progress: number;
    }[]
  >([]);
  //#endregion

  //#region api
  const {
    data: dataQuestion,
    isLoading: isLoadingQuestion,
    isError: isErrorQuestion,
  } = useGetQuestionQuery(id as string, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const [
    addQuestion,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddQuestionMutation();

  const [
    updateQuestion,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateQuestionMutation();

  const [uploadQuestionImageApi] = useUploadQuestionImageMutation();

  //#endregion

  const validationSchema = Yup.object<CreateQuestionRequest>().shape({
    contents: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required("Type is required"),
        text: Yup.string().when("type", {
          is: "text",
          then: () =>
            Yup.string()
              .nullable()
              .test(
                "text-not-empty",
                t("isNotEmpty", { displayName: "text" }),
                (val) => (val ?? "").trim().length > 0,
              ),
        }),
        image: Yup.string().optional().nullable(),
        tempId: Yup.string().optional().nullable(),
      }),
    ),
    model: Yup.string().required(
      t("isNotEmpty", {
        displayName: "questionModel",
      }),
    ),
    timeDuration: Yup.string().required(
      t("isNotEmpty", {
        displayName: "timeDuration",
      }),
    ),
    marks: Yup.string().required(
      t("isNotEmpty", {
        displayName: "marks",
      }),
    ),
    lessonId: Yup.string().required(
      t("isNotEmpty", {
        displayName: "lesson",
      }),
    ),
    optionA: Yup.object({
      en: Yup.string(),
      ar: Yup.string().required(
        t("isNotEmpty", {
          displayName: "arabicValue",
        }),
      ),
    }),
    optionB: Yup.object({
      en: Yup.string(),
      ar: Yup.string().required(
        t("isNotEmpty", {
          displayName: "arabicValue",
        }),
      ),
    }),
    optionC: Yup.object({
      en: Yup.string().nullable(),
      ar: Yup.string().nullable(),
    }),
    optionD: Yup.object({
      en: Yup.string().nullable(),
      ar: Yup.string().nullable(),
    }),
    correctOption: Yup.string().required(
      t("isNotEmpty", {
        displayName: "correctOption",
      }),
    ),
  });

  //#region effects

  useEffect(() => {
    if (id && dataQuestion) {
      const contentWithTempIds = dataQuestion.contents.map((qc: any) => ({
        ...qc,
        tempId: generateUniqueId(),
      }));

      setImagesProgress(
        contentWithTempIds
          .filter((c: any) => c.type === "image")
          .map((item: any) => ({
            isError: false,
            isLoading: false,
            isSuccess: true,
            error: null,
            tempId: item.tempId,
            progress: 100,
            response: {
              image: item.image,
              tempId: item.tempId,
            },
          })),
      );

      setInitialValues({
        contents: contentWithTempIds,
        lessonId: dataQuestion.lessonId,
        correctOption: dataQuestion.correctOption,
        marks: Number(dataQuestion.marks),
        model: dataQuestion.model,
        optionA: dataQuestion.optionA,
        optionB: dataQuestion.optionB,
        optionC: dataQuestion.optionC,
        optionD: dataQuestion.optionD,
        timeDuration: dataQuestion.timeDuration,
        displayImageId: dataQuestion.displayImage?.id,
        skills: [],
      });
    }

    if (!id) {
      setImagesProgress([]);
      setInitialValues({
        contents: [],
        model,
        timeDuration: "3:00",
        marks: 1,
        displayImageId: "",
        lessonId,
        skills: [],
        optionA: { en: "", ar: "" },
        optionB: { en: "", ar: "" },
        optionC: { en: "", ar: "" },
        optionD: { en: "", ar: "" },
        correctOption: QuestionOptionEnum.OPTION_A,
      });
    }
  }, [id, dataQuestion, lessonId, model]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      formRef?.current?.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      toast.success(t("savedSuccessfully"), { autoClose: 3000 });
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    setImagesProgress([]);
    onCloseClick(!show);
  };

  const onChangeQuestionImage = (
    event: ChangeEvent<HTMLInputElement>,
    tempId: string,
  ) => {
    const { target } = event;
    const { files } = target;
    if (!files || files.length === 0) return;
    const imageFile = files[0];
    if (
      !imageFile.name.match(
        /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/,
      )
    ) {
      toast.error(t("selectValidImage"), { autoClose: 3000 });
      return;
    }
    setImagesProgress((prev) =>
      prev.map((ip) =>
        ip.tempId === tempId
          ? {
              ...ip,
              error: null,
              isError: false,
              isLoading: true,
              isSuccess: false,
              response: null,
              progress: 0,
            }
          : ip,
      ),
    );
    uploadQuestionImageApi({
      file: imageFile,
      tempId,
      onProgress: (progress, tid) => {
        setImagesProgress((prev) =>
          prev.map((ip) =>
            ip.tempId === tid ? { ...ip, progress, isLoading: true } : ip,
          ),
        );
      },
    })
      .unwrap()
      .then((response) => {
        setImagesProgress((prev) =>
          prev.map((ip) =>
            ip.tempId === tempId
              ? {
                  ...ip,
                  error: null,
                  isError: false,
                  isLoading: false,
                  isSuccess: true,
                  response,
                  progress: 100,
                }
              : ip,
          ),
        );
      })
      .catch((err) => {
        const errMsg =
          (err as any)?.data?.message || (err as any)?.message || t("error");
        setImagesProgress((prev) =>
          prev.map((ip) =>
            ip.tempId === tempId
              ? {
                  ...ip,
                  error: errMsg,
                  isError: true,
                  isLoading: false,
                  isSuccess: false,
                  response: null,
                }
              : ip,
          ),
        );
        toast.error(errMsg, { autoClose: 3000 });
      });
  };

  const handleFormSubmit = async (values: any) => {
    //#region custom validations
    if (values.contents.length === 0) {
      toast.error(t("atLeast1ImageOrTextIsRequired"), {
        autoClose: 3000,
      });
      return;
    }
    if (
      imagesProgress.some(
        (ip) => ip.response === null || ip.response.image === null,
      )
    ) {
      toast.error(t("uploadImageOrRemoveImageControl"), {
        autoClose: 3000,
      });
      return;
    }
    //#endregion

    values.contents = values.contents.map((item: QuestionContentDto) => {
      if (item.type === "image") {
        const uploadedImagePath = imagesProgress.find(
          (ip) => ip.tempId === item.tempId,
        )?.response?.image;
        return {
          ...item,
          image: uploadedImagePath,
        };
      } else {
        return item;
      }
    });

    values.contents = values.contents.map((item: QuestionContentDto) =>
      _.omit(item, "tempId"),
    );

    const payload = {
      ...values,
      optionA: {
        ...values.optionA,
        en: values.optionA.ar,
      },
      optionB: {
        ...values.optionB,
        en: values.optionB.ar,
      },
      optionC: {
        ...values.optionC,
        en: values.optionC.ar,
      },
      optionD: {
        ...values.optionD,
        en: values.optionD.ar,
      },
    };
    if (id) {
      await updateQuestion({ id, data: payload }).unwrap();
    } else {
      await addQuestion(payload).unwrap();
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-xl"
        scrollable={true}
      >
        <ModalBody>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              setFieldValue,
              touched,
              setFieldError,
              errors,
              setFieldTouched,
              handleBlur,
              handleChange,
            }) => (
              <Form id="form1">
                <FieldArray name="contents">
                  {({ remove, push }) => (
                    <>
                      {values.contents?.length > 0 &&
                        values.contents?.map(
                          (
                            field: {
                              type: string;
                              text?: string;
                              image?: string;
                              tempId?: string;
                            },
                            index: number,
                          ) => (
                            <div
                              key={`content-${field.tempId ?? (field as any).id ?? index}`}
                            >
                              {field.type === "text" && (
                                <Row className="p-2 mb-2">
                                  <Col xl="12">
                                    <div className="mb-3">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label
                                          htmlFor={`contents.${index}.text`}
                                          className="form-label fs-5"
                                        >
                                          {t("questionText")}
                                          <span className="text-danger">*</span>
                                        </Label>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => {
                                            remove(index);
                                          }}
                                        >
                                          {t("removeQuestionText")}
                                        </button>
                                      </div>
                                      <AppRichEditor
                                        key={`content-text-${field.tempId ?? (field as any).id ?? index}`}
                                        initialValue={field.text ?? ""}
                                        onChange={(data: any) => {
                                          setFieldValue(
                                            `contents.${index}.text`,
                                            data,
                                          );
                                        }}
                                        onKeyUp={(data: any) => {
                                          if ((data ?? "").trim().length === 0) {
                                            setFieldError(
                                              `contents.${index}.text`,
                                              t("isNotEmpty", {
                                                displayName: "arabicValue",
                                              }),
                                            );
                                          } else {
                                            setFieldError(
                                              `contents.${index}.text`,
                                              "",
                                            );
                                          }
                                        }}
                                        onFocus={() => {
                                          setFieldTouched(
                                            `contents.${index}.text`,
                                            true,
                                            true,
                                          );
                                        }}
                                      />
                                      {errors &&
                                      errors.contents &&
                                      errors.contents.length &&
                                      errors.contents.length > 0 ? (
                                        <Label className="invalid-feedback d-block">
                                          {
                                            (errors.contents[index] as any)
                                              ?.text
                                          }
                                        </Label>
                                      ) : null}
                                    </div>
                                  </Col>
                                </Row>
                              )}
                              {field.type === "image" && (
                                <Row className="p-2 mb-2">
                                  <Col sm={12} className="text-center">
                                    <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                                      <img
                                        id={`image_${field.tempId}`}
                                        src={
                                          imagesProgress.find(
                                            (i) => i.tempId === field.tempId,
                                          )?.response?.image ||
                                          dataQuestion?.contents[index]
                                            ?.image ||
                                          profile
                                        }
                                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                                        style={{
                                          height: "10.5rem",
                                          width: "10.5rem",
                                        }}
                                        alt="user-profile"
                                      />
                                      <div
                                        className="avatar-xl p-0 rounded-circle profile-photo-edit"
                                        style={{
                                          right: "2px",
                                          bottom: "8px",
                                        }}
                                      >
                                        <Input
                                          id={`question_image_${field.tempId}`}
                                          type="file"
                                          className="profile-img-file-input"
                                          accept={IMAGE_ACCEPT}
                                          onChange={(
                                            event: ChangeEvent<HTMLInputElement>,
                                          ) =>
                                            onChangeQuestionImage(
                                              event,
                                              field.tempId as string,
                                            )
                                          }
                                          disabled={
                                            imagesProgress.find(
                                              (i) => i.tempId === field.tempId,
                                            )?.isLoading
                                          }
                                        />
                                        <Label
                                          htmlFor={`question_image_${field.tempId}`}
                                          className="profile-photo-edit avatar-xs"
                                        >
                                          <span className="avatar-title rounded-circle bg-light text-body">
                                            {imagesProgress.find(
                                              (i) => i.tempId === field.tempId,
                                            )?.isLoading && <Spinner />}
                                            {!imagesProgress.find(
                                              (i) => i.tempId === field.tempId,
                                            )?.isLoading && (
                                              <i className="ri-camera-fill"></i>
                                            )}
                                          </span>
                                        </Label>
                                      </div>
                                      {imagesProgress.find(
                                        (i) => i.tempId === field.tempId,
                                      )?.isLoading && (
                                        <div className="mt-2 w-100 px-2">
                                          <div
                                            className="progress"
                                            style={{ height: 6 }}
                                          >
                                            <div
                                              className="progress-bar progress-bar-striped progress-bar-animated"
                                              role="progressbar"
                                              style={{
                                                width: `${
                                                  imagesProgress.find(
                                                    (i) =>
                                                      i.tempId === field.tempId,
                                                  )?.progress ?? 0
                                                }%`,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                  <Col sm={12} className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => {
                                        setImagesProgress((prev) =>
                                          prev.filter(
                                            (i) => i.tempId !== field.tempId,
                                          ),
                                        );
                                        remove(index);
                                      }}
                                    >
                                      {t("removeQuestionImage")}
                                    </button>
                                  </Col>
                                </Row>
                              )}
                            </div>
                          ),
                        )}
                      {values.contents.length === 0 && (
                        <div className="d-flex align-items-center">
                          <i className="ri-information-line text-info pe-1"></i>
                          <span className="text-info">
                            {t("atLeast1ImageOrTextIsRequired")}
                          </span>
                        </div>
                      )}
                      <div className="d-flex py-3 justify-content-end align-items-center">
                        <button
                          type="button"
                          className="btn btn-primary mx-1"
                          onClick={() => {
                            const newId = generateUniqueId();
                            push({
                              type: "text",
                              text: "",
                              id: newId,
                            });
                          }}
                        >
                          {t("addText")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            const newId = generateUniqueId();
                            setImagesProgress((prev) => [
                              ...prev,
                              {
                                error: null,
                                isError: false,
                                isLoading: false,
                                isSuccess: false,
                                response: null,
                                tempId: newId,
                                progress: 0,
                              },
                            ]);
                            push({
                              type: "image",
                              image: null,
                              tempId: newId,
                            });
                          }}
                        >
                          {t("addImage")}
                        </button>
                      </div>
                    </>
                  )}
                </FieldArray>
                <>
                  <Row>
                    <Col xl="6">
                      <Label htmlFor="optionA.ar" className="form-label">
                        {t("answerA")}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={
                            id && dataQuestion
                              ? dataQuestion.optionA?.ar || ""
                              : ""
                          }
                          onChange={(data: any) => {
                            setFieldValue("optionA.ar", data);
                          }}
                          onKeyUp={(data: any) => {
                            if (data.trim().length == 0) {
                              console.log("length 0");
                              setFieldError(
                                "optionA.ar",
                                t("isNotEmpty", {
                                  displayName: "arabicValue",
                                }),
                              );
                            } else {
                              setFieldError("optionA.ar", "");
                            }
                          }}
                          onFocus={() => {
                            setFieldTouched("optionA.ar", true, true);
                          }}
                        />
                        {touched.optionA?.ar && errors.optionA?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionA?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                    <Col xl="6">
                      <Label htmlFor="optionB.ar" className="form-label">
                        {t("answerB")}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={
                            id && dataQuestion
                              ? dataQuestion.optionB?.ar || ""
                              : ""
                          }
                          onChange={(data: any) => {
                            setFieldValue("optionB.ar", data);
                          }}
                          onKeyUp={(data: any) => {
                            if (data.trim().length == 0) {
                              setFieldError(
                                "optionB.ar",
                                t("isNotEmpty", {
                                  displayName: "arabicValue",
                                }),
                              );
                            } else {
                              setFieldError("optionB.ar", "");
                            }
                          }}
                          onFocus={() => {
                            setFieldTouched("optionB.ar", true, true);
                          }}
                        />
                        {touched.optionB?.ar && errors.optionB?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionB?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="6">
                      <Label htmlFor="optionC.ar" className="form-label">
                        {t("answerC")}
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={
                            id && dataQuestion
                              ? dataQuestion.optionC?.ar || ""
                              : ""
                          }
                          onChange={(data: any) => {
                            setFieldValue("optionC.ar", data);
                          }}
                          onFocus={() => {
                            setFieldTouched("optionC.ar", true, true);
                          }}
                        />
                        {touched.optionC?.ar && errors.optionC?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionC?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                    <Col xl="6">
                      <div className="mb-3">
                        <Label htmlFor="optionD.ar" className="form-label">
                          {t("answerD")}
                        </Label>
                        <AppRichEditor
                          initialValue={
                            id && dataQuestion
                              ? dataQuestion.optionD?.ar || ""
                              : ""
                          }
                          onChange={(data: any) => {
                            setFieldValue("optionD.ar", data);
                          }}
                          onFocus={() => {
                            setFieldTouched("optionD.ar", true, true);
                          }}
                        />
                        {touched.optionD?.ar && errors.optionD?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionD?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="12">
                      <Row>
                        <Col sm={4}>
                          <div className="mb-3">
                            <Label
                              htmlFor="correctOption"
                              className="form-label"
                            >
                              {t("correctAnswer")}
                            </Label>
                            <Input
                              type="select"
                              id="correctOption"
                              name="correctOption"
                              className="form-select"
                              aria-label={t("chooseCorrectAnswer")}
                              placeholder={t("chooseCorrectAnswer")}
                              value={values.correctOption}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value={""}>
                                {t("chooseCorrectAnswer")}{" "}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_A}
                                key={"A"}
                                className="text-capitalize"
                              >
                                {t("A")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_B}
                                key={"B"}
                                className="text-capitalize"
                              >
                                {t("B")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_C}
                                key={"C"}
                                className="text-capitalize"
                              >
                                {t("C")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_D}
                                key={"D"}
                                className="text-capitalize"
                              >
                                {t("D")}
                              </option>
                            </Input>
                            {touched.correctOption && errors.correctOption ? (
                              <FormFeedback type="invalid">
                                {errors.correctOption as string}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col sm={4}>
                          <Label htmlFor="marks" className="form-label">
                            {t("marks")}
                          </Label>
                          <div className="mb-3">
                            <div className="input-step w-100">
                              <button
                                type="button"
                                className="minus"
                                onClick={() => {
                                  if (values.marks > 1)
                                    setFieldValue("marks", values.marks + -1);
                                }}
                              >
                                –
                              </button>
                              <Input
                                type="text"
                                className="product-quantity w-100"
                                value={values.marks}
                                name="marks"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                readOnly
                              />
                              <button
                                type="button"
                                className="plus"
                                onClick={() => {
                                  setFieldValue("marks", values.marks + 1);
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <Label htmlFor="time" className="form-label">
                            {t("time")}
                          </Label>
                          <div className="mb-3">
                            <TimeInput
                              id="time"
                              name="time"
                              value={values.timeDuration}
                              className="form-control"
                              onChangeValue={(value: string) => {
                                setFieldValue("timeDuration", value);
                              }}
                            />
                            {touched.timeDuration && errors.timeDuration ? (
                              <FormFeedback type="invalid">
                                {errors.timeDuration as string}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </>
              </Form>
            )}
          </Formik>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            form="form1"
            inProgress={isLoadingAdd || isLoadingUpdate}
            disabled={
              isLoadingQuestion ||
              isLoadingAdd ||
              isLoadingUpdate ||
              isErrorQuestion ||
              imagesProgress.some((ip) => ip.isLoading)
            }
          ></ActivityButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};
